/**
 * Home — the mission-control surface for established users (V1-11).
 *
 * Three zones:
 *   1. GREETING — time-aware greeting + name + archetype chip + dynamic
 *      status line that changes based on pipeline state.
 *   2. PIPELINE — horizontal funnel (Saved → Applied → Interview → Offer)
 *      with counts. Click any pill to drill into Dashboard filtered by status.
 *   3. ACTION CARDS — 3 cards with hierarchy: primary (always Search), then
 *      a contextual secondary, then a tertiary that varies by state.
 *
 * Rendered ONLY when /api/user/state confirms the user has key + resume +
 * at least one application. Earlier-stage users are routed elsewhere by
 * HomeOrLanding before they ever see this component.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Typography, Stack, Chip, Card, CardActionArea,
  CardContent, Skeleton, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuth } from '../context/AuthContext';
import { getHomeDashboard } from '../services/api';

const KNOWN_ARCHETYPES = ['climber', 'rebuilder', 'explorer', 'specialist'];

// Pipeline stages in funnel order. Status keys must match backend values.
// "rejected" intentionally excluded from the funnel visual — it's a terminal
// outcome that doesn't fit the linear narrative; users can still see rejected
// counts on the Dashboard via filter chips.
const PIPELINE_STAGES = [
  { key: 'saved', icon: '💾' },
  { key: 'applied', icon: '✉️' },
  { key: 'interview_scheduled', icon: '📅' },
  { key: 'offer', icon: '🎉' },
];

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getHomeDashboard()
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Loading skeleton — keeps layout stable so the page doesn't jump on hydrate.
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Skeleton variant="text" width="60%" height={48} />
        <Skeleton variant="text" width="40%" height={24} sx={{ mb: 4 }} />
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 4 }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Skeleton variant="rectangular" height={140} sx={{ flex: 1, borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={140} sx={{ flex: 1, borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={140} sx={{ flex: 1, borderRadius: 2 }} />
        </Stack>
      </Container>
    );
  }

  // Defensive: if dashboard fetch failed, fall back to a minimal greeting +
  // a single CTA so the page is never broken.
  if (!data) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          {t('home.greeting_generic')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {t('home.fetch_failed')}
        </Typography>
        <Card variant="outlined" sx={{ borderRadius: 2, maxWidth: 400 }}>
          <CardActionArea onClick={() => navigate('/jobs')} sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <SearchIcon color="primary" />
              <Typography fontWeight={500}>{t('home.action_search_primary')}</Typography>
            </Stack>
          </CardActionArea>
        </Card>
      </Container>
    );
  }

  const { pipeline, last_application_days_ago, profile_summary } = data;
  const firstName =
    profile_summary?.name
    || (user?.user_metadata?.full_name || user?.email || '').split(/[\s@]/)[0]
    || null;
  const archetype = profile_summary?.archetype;
  const showArchetype = archetype && KNOWN_ARCHETYPES.includes(archetype);

  const totalActive =
    pipeline.saved + pipeline.applied + pipeline.interview_scheduled + pipeline.offer;

  // Dynamic status line — cycles through priorities. The first matching
  // condition wins. Order is intentional (most-actionable first).
  const statusLine = (() => {
    if (pipeline.interview_scheduled > 0) {
      return t('home.status_has_interviews', { count: pipeline.interview_scheduled });
    }
    if (pipeline.applied > 0 && last_application_days_ago !== null) {
      return t('home.status_has_applications', {
        count: pipeline.applied,
        days: last_application_days_ago,
      });
    }
    if (pipeline.saved > 0) {
      return t('home.status_has_saved', { count: pipeline.saved });
    }
    return t('home.status_empty');
  })();

  // Secondary action — the contextual one. Evaluates from most-actionable
  // (interview prep) down. Falls back to "view applications" when nothing
  // jumps out.
  const secondaryAction = (() => {
    if (pipeline.saved > 0) {
      return {
        icon: <AutoFixHighIcon />,
        label: t('home.action_tailor_secondary'),
        sublabel: t('home.action_tailor_sub', { count: pipeline.saved }),
        onClick: () => navigate('/dashboard?status=saved'),
      };
    }
    if (pipeline.interview_scheduled > 0) {
      return {
        icon: <AssignmentIcon />,
        label: t('home.action_review_interviews'),
        sublabel: t('home.action_review_interviews_sub'),
        onClick: () => navigate('/dashboard?status=interview_scheduled'),
      };
    }
    return {
      icon: <AssignmentIcon />,
      label: t('home.action_view_applications'),
      sublabel: t('home.action_view_applications_sub', { count: totalActive }),
      onClick: () => navigate('/dashboard'),
    };
  })();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* ── 1. GREETING ───────────────────────────────────────────────
           Simplified to "Welcome, {name}" — no time-of-day variant. The
           archetype chip uses the full "Archetype: {label}" form so users
           who don't know the term still get context. */}
      <Box mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h4" fontWeight={600}>
            {firstName
              ? t('home.greeting_with_name', { name: firstName })
              : t('home.greeting_generic')}
          </Typography>
          {showArchetype && (
            <Chip
              label={t('resume.archetype_label', {
                name: t(`resume.archetype_${archetype}`),
              })}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Stack>
        <Typography variant="body1" color="text.secondary" mt={0.5}>
          {statusLine}
        </Typography>
      </Box>

      {/* ── 1.5 INSIGHTS — proactive nudges (V1-11 / Nav 7) ──────────────
           Renders 0–3 contextual cards from /api/home insights. Hidden
           entirely when the array is empty (no clutter for fresh users). */}
      {Array.isArray(data.insights) && data.insights.length > 0 && (
        <Box mb={4}>
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={600}
            sx={{ letterSpacing: 0.5 }}
          >
            {t('home.insights_label')}
          </Typography>
          <Stack spacing={1.5} mt={1}>
            {data.insights.map((insight, idx) => (
              <InsightCard
                key={`${insight.type}-${idx}`}
                insight={insight}
                onAction={(path) => navigate(path)}
                t={t}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* ── 2. PIPELINE ───────────────────────────────────────────────
           4 equal columns (25% each) so all stages fit without horizontal
           scroll, even on narrow phones. Empty stages stay visible but
           muted; non-empty get a subtle primary tint to draw the eye. */}
      <Box mb={4}>
        <Typography
          variant="overline"
          color="text.secondary"
          fontWeight={600}
          sx={{ letterSpacing: 0.5 }}
        >
          {t('home.pipeline_label')}
        </Typography>
        <Box
          mt={1}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1,
          }}
        >
          {PIPELINE_STAGES.map(stage => {
            const count = pipeline[stage.key] || 0;
            const isEmpty = count === 0;
            return (
              <Card
                key={stage.key}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  bgcolor: isEmpty ? 'transparent' : 'background.paper',
                  borderColor: isEmpty ? 'divider' : 'primary.light',
                  opacity: isEmpty ? 0.6 : 1,
                  transition: 'all 0.15s',
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/dashboard?status=${stage.key}`)}
                  sx={{
                    px: { xs: 1, sm: 1.5 },
                    py: 1.25,
                    height: '100%',
                  }}
                  disabled={isEmpty}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: 10, sm: 11 },
                      lineHeight: 1.2,
                      display: 'block',
                    }}
                  >
                    {t(`home.pipeline_${stage.key}`)}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    mt={0.25}
                    sx={{ fontSize: { xs: 20, sm: 22 } }}
                  >
                    {count}
                  </Typography>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      </Box>

      {/* ── 3. ACTION CARDS ─────────────────────────────────────────── */}
      <Box>
        <Typography
          variant="overline"
          color="text.secondary"
          fontWeight={600}
          sx={{ letterSpacing: 0.5 }}
        >
          {t('home.next_action_label')}
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          mt={1}
        >
          {/* Primary — always Search. Filled-style to draw the eye. */}
          <ActionCard
            icon={<SearchIcon />}
            label={t('home.action_search_primary')}
            sublabel={t('home.action_search_sub')}
            onClick={() => navigate('/jobs')}
            variant="primary"
          />
          {/* Secondary — contextual */}
          <ActionCard
            icon={secondaryAction.icon}
            label={secondaryAction.label}
            sublabel={secondaryAction.sublabel}
            onClick={secondaryAction.onClick}
            variant="secondary"
          />
          {/* Tertiary — always Update Resume (the third pillar of the system) */}
          <ActionCard
            icon={<DescriptionIcon />}
            label={t('home.action_resume_tertiary')}
            sublabel={t('home.action_resume_sub')}
            onClick={() => navigate('/resume')}
            variant="tertiary"
          />
        </Stack>
      </Box>
    </Container>
  );
}

/**
 * InsightCard — single proactive nudge rendered on the Home page.
 *
 * Translates the backend's structured insight into a friendly card with
 * icon, message, and optional CTA. Severity drives accent color:
 *   - "warning" → amber border + warning icon
 *   - "info"    → primary tint + lightbulb icon
 */
function InsightCard({ insight, onAction, t }) {
  const isWarning = insight.severity === 'warning';
  const Icon = isWarning ? WarningAmberIcon : LightbulbOutlinedIcon;

  // The backend ships message_key + message_params; we hand them to t() so
  // pluralization and locale work out of the box. Same for the action label.
  const message = t(insight.message_key, insight.message_params || {});
  const actionLabel = insight.action ? t(insight.action.label_key) : null;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: isWarning ? 'warning.light' : 'primary.light',
        bgcolor: isWarning ? 'warning.50' : 'primary.50',
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Icon
              sx={{
                color: isWarning ? 'warning.dark' : 'primary.main',
                fontSize: 22,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                lineHeight: 1.5,
              }}
            >
              {message}
            </Typography>
          </Stack>
          {actionLabel && insight.action?.path && (
            <Button
              size="small"
              variant="contained"
              color={isWarning ? 'warning' : 'primary'}
              onClick={() => onAction(insight.action.path)}
              sx={{
                whiteSpace: 'nowrap',
                alignSelf: { xs: 'flex-end', sm: 'center' },
              }}
            >
              {actionLabel}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function ActionCard({ icon, label, sublabel, onClick, variant }) {
  // Visual hierarchy via background + border, not size. Primary gets the
  // navy fill; secondary gets a tinted background; tertiary stays neutral.
  const stylesByVariant = {
    primary: {
      bgcolor: 'primary.main',
      color: 'common.white',
      borderColor: 'primary.main',
      iconColor: 'common.white',
      sublabelColor: 'rgba(255,255,255,0.75)',
    },
    secondary: {
      bgcolor: 'background.paper',
      color: 'text.primary',
      borderColor: 'primary.light',
      iconColor: 'primary.main',
      sublabelColor: 'text.secondary',
    },
    tertiary: {
      bgcolor: 'background.paper',
      color: 'text.primary',
      borderColor: 'divider',
      iconColor: 'text.secondary',
      sublabelColor: 'text.secondary',
    },
  };
  const s = stylesByVariant[variant] || stylesByVariant.tertiary;

  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        borderRadius: 2,
        bgcolor: s.bgcolor,
        color: s.color,
        borderColor: s.borderColor,
        transition: 'transform 0.12s, box-shadow 0.12s',
        '&:hover': {
          borderColor: s.borderColor,
          boxShadow: 2,
        },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ color: s.iconColor, mb: 1 }}>
            {icon}
          </Box>
          <Typography fontWeight={600} mb={0.5}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: s.sublabelColor, lineHeight: 1.4 }}>
            {sublabel}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

